<template>
  <div v-if="dataFinishedLoading" class="projectApplication">
    <template v-if="!isSubmitted">
      <Stepper
        :current-step="currentStepper"
        :step-list="stepperList"
        :disabled-steps="disabledStepperItems"
        @selectedItem="setStepperItem($event, setConfirmValues)"
      ></Stepper>

      <ValidationObserver v-slot="{ invalid }">
        <form class="form">
          <ValidationProvider
            v-for="(field, index) in currentStep.fields"
            :key="field.name"
            v-slot="{ errors }"
            :name="field.name"
            :rules="field.rules"
            persist
          >
            <div v-if="index == 0" id="formHeader">
              <span id="currentStepTitle">
                <h5>{{ $t(currentStep.title) }}</h5>
              </span>
            </div>

            <div class="inputs">
              <TextArea
                v-if="field.textArea"
                :id="field.style"
                :name="$t(field.title)"
                :value="field.value"
                :placeholder="$t(field.placeholder)"
                :style-type="field.textAreaStyle"
                :plaintext="field.plaintext"
                :direction="field.direction"
                :rows="field.rows"
                :readonly="field.readonly"
                @input="field.value = $event"
              >
              </TextArea>

              <FormInput
                v-if="field.formInput"
                :name="$t(field.title)"
                :value="field.value"
                :placeholder="$t(field.placeholder)"
                :readonly="field.readonly"
                @input="field.value = $event"
              ></FormInput>

              <SearchSelect
                v-if="field.searchSelect"
                :name="$t(field.title)"
                :options="field.options"
                :selected-option="field.selectedOption"
                :clearable="field.clearable"
                @handleChange="field.selectedOption = $event"
              ></SearchSelect>

              <ExplanatoryText v-if="field.legend" :style-type="field.style" :value="field.text"></ExplanatoryText>

              <span class="stepErrorMessages">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>

          <div class="stepButtons">
            <PreviousStepButton v-if="activeStep > 0" @clickEvent="previousStep"> </PreviousStepButton>

            <SubmitButton
              v-if="isLastStep"
              :disabled="invalid || SelectedSameOption(steps[0].fields)"
              @clickEvent="createProjectApplication"
            >
            </SubmitButton>

            <NextStepButton v-else :disabled="invalid || SelectedSameOption(steps[0].fields)" @clickEvent="nextStep(setConfirmValues)">
            </NextStepButton>
          </div>

          <b-alert id="successMessage" :show="dismissCountDown" fade variant="success" @dismiss-count-down="countDownChanged">
            {{ $t(localeConsts.successMessage) }}
          </b-alert>
        </form>
      </ValidationObserver>
    </template>

    <div v-else class="submission-success-container">
      <b-alert show variant="info" class="text-center mt-4">
        <h4 class="mb-3">Erfolgreich eingereicht</h4>
        <p>Ihre Bewerbung wird von einem Betreuer ausgewertet, bitte haben Sie noch ein wenig Geduld.</p>
      </b-alert>
    </div>
  </div>
</template>

<script>
import Stepper from '@/components/util/Stepper'
import ExplanatoryText from '@/components/util/Text/ExplanatoryText'

import TextArea from '@/components/util/Inputs/TextArea'
import FormInput from '@/components/util/Inputs/FormInput'
import SearchSelect from '@/components/util/Inputs/SearchSelect'

import NextStepButton from '@/components/util/Buttons/FormButtons/NextStepButton'
import PreviousStepButton from '@/components/util/Buttons/FormButtons/PreviousStepButton'
import SubmitButton from '@/components/util/Buttons/FormButtons/SubmitButton'

import { multiFormMixin } from '../../../mixins/multiFormMixin'
import { generalHelperMethodsMixin } from '../../../mixins/generalHelperMethodsMixin'
import { manualValidationMixin } from '../../../mixins/manualValidationMixin'
import { filtersMixin } from '../../../mixins/filtersMixin'

import mapGettersMixin from '../../../mixins/mapGettersMixin'
import { alertMessagesMixin } from '../../../mixins/alertMessagesMixin'
import { apiFetchMethodsMixin } from '../../../mixins/apiFetchMethodsMixin'

import axios from '../../../../axios'

export default {
  name: 'ProjectApplication',
  components: { Stepper, ExplanatoryText, TextArea, FormInput, SearchSelect, NextStepButton, PreviousStepButton, SubmitButton },
  mixins: [
    multiFormMixin,
    generalHelperMethodsMixin,
    manualValidationMixin,
    filtersMixin,
    mapGettersMixin,
    alertMessagesMixin,
    apiFetchMethodsMixin
  ],

  data() {
    return {
      isSubmitted: false,

      localeConsts: {
        successMessage: 'ALERT.PROJECT_APPLICATION_SUCCESSFULLY_CREATED_CHANGED'
      },

      currentStepper: 1,
      stepperList: ['PROJECTAPPLICATION.PICK_PROJECT', 'PROJECTAPPLICATION.ENTER_PREFERENCES', 'PROJECTAPPLICATION.CONFIRM'],
      disabledStepperItems: [false, true, true],

      activeStep: 0,
      steps: [
        {
          title: 'PROJECTAPPLICATION.PICK_PROJECT_HEADER',
          fields: [
            {
              name: 'firstPriority',
              title: 'PROJECTAPPLICATION.YOUR_FIRST_PRIORITY',
              options: [],
              selectedOption: null,
              clearable: true,
              searchSelect: true,
              rules: 'required'
            },
            {
              name: 'secondPriority',
              title: 'PROJECTAPPLICATION.YOUR_SECOND_PRIORITY',
              options: [],
              selectedOption: null,
              clearable: true,
              searchSelect: true
            },
            {
              name: 'thirdPriority',
              title: 'PROJECTAPPLICATION.YOUR_THIRD_PRIORITY',
              options: [],
              selectedOption: null,
              clearable: true,
              searchSelect: true
            }
          ]
        },

        {
          title: 'PROJECTAPPLICATION.ENTER_PREFERENCES_HEADER',
          fields: [
            {
              name: 'preferences',
              placeholder: 'PROJECTAPPLICATION.ENTER_PREFERENCES_LEGEND',
              rows: 6,
              rules: 'required|max:255',
              value: '',
              textArea: true
            }
          ]
        },

        {
          title: 'PROJECTAPPLICATION.CONFIRM_HEADER',
          fields: [
            {
              name: 'selectedProject1',
              title: 'PROJECTAPPLICATION.YOUR_SELECTED_PROJECTS',
              value: '',
              readonly: true,
              formInput: true
            },
            {
              name: 'selectedProjects2',
              title: ' ',
              value: '',
              readonly: true,
              formInput: true
            },
            {
              name: 'selectedProjects3',
              title: ' ',
              value: '',
              readonly: true,
              formInput: true
            },
            {
              name: 'additionalPreferences',
              title: 'PROJECTAPPLICATION.YOUR_ADDITIONAL_PREFERENCES',
              rows: 6,
              value: '',
              readonly: true,
              textArea: true,
              direction: 'horizontal'
            }
          ]
        }
      ],

      dataFinishedLoading: false
    }
  },

  async created() {
    await this.loadData()
    this.dataFinishedLoading = true
  },

  methods: {
    async loadData() {
      await this.fetchActiveCCPProjects()
      await this.fetchProjectApplicationData()

      this.setProjects()
      this.setProjectApplicationData()
      return true
    },

    /*api write methods */

    createProjectApplication() {
      const wishProjectOneId = this.steps[0].fields[0].selectedOption !== null ? this.steps[0].fields[0].selectedOption.value : null
      const wishProjectTwoId = this.steps[0].fields[1].selectedOption !== null ? this.steps[0].fields[1].selectedOption.value : null
      const wishProjectThreeId = this.steps[0].fields[2].selectedOption !== null ? this.steps[0].fields[2].selectedOption.value : null

      const preferences = this.steps[1].fields[0].value !== null ? this.steps[1].fields[0].value : null

      const newProjectApplication = {
        wish_one: wishProjectOneId,
        wish_two: wishProjectTwoId,
        wish_three: wishProjectThreeId,
        comments: preferences
      }

      axios.post('project-applications/', newProjectApplication).then(() => {
        this.showAlert()
        this.isSubmitted = true
        window.scrollTo(0, 0)
      })
    },

    /*setter methods*/

    setProjectApplicationData() {
      if (this.fetchedProjectApplicationData.length > 0) {
        //selected projects
        this.steps[0].fields[0].selectedOption = {
          value: this.fetchedProjectApplicationData[0].wish_one,
          text: this.findOptionTextByValue(this.steps[0].fields[0].options, this.fetchedProjectApplicationData[0].wish_one)
        }
        this.steps[0].fields[1].selectedOption = {
          value: this.fetchedProjectApplicationData[0].wish_two,
          text: this.findOptionTextByValue(this.steps[0].fields[1].options, this.fetchedProjectApplicationData[0].wish_two)
        }
        this.steps[0].fields[2].selectedOption = {
          value: this.fetchedProjectApplicationData[0].wish_three,
          text: this.findOptionTextByValue(this.steps[0].fields[2].options, this.fetchedProjectApplicationData[0].wish_three)
        }

        //comment
        this.steps[1].fields[0].value = this.fetchedProjectApplicationData[0].comments

        this.disabledStepperItems = [false, false, false]
        this.isSubmitted = true
      }
    },

    setProjects() {
      const optionsData = this.fetchedProjectsData.map((item) => ({
        value: item.id,
        text: `${item.name}`
      }))

      this.steps[0].fields[0].options = optionsData
      this.steps[0].fields[1].options = optionsData
      this.steps[0].fields[2].options = optionsData
    },

    setConfirmValues() {
      //selected projects
      this.steps[2].fields[0].value = `1. ${
        this.steps[0].fields[0].selectedOption !== null ? this.steps[0].fields[0].selectedOption.text : ''
      }`
      this.steps[2].fields[1].value = `2. ${
        this.steps[0].fields[1].selectedOption !== null ? this.steps[0].fields[1].selectedOption.text : ''
      }`
      this.steps[2].fields[2].value = `3. ${
        this.steps[0].fields[2].selectedOption !== null ? this.steps[0].fields[2].selectedOption.text : ''
      }`

      //comments
      this.steps[2].fields[3].value = this.steps[1].fields[0].value !== null ? this.steps[1].fields[0].value : ''
    }
  }
}
</script>

<style lang="scss" scoped>
$text-color: #8b69a9;
$error-text-color: red;

.projectApplication {
  width: 100%;
  color: $text-color;
}

#formHeader {
  display: flex;
  margin-top: 15px;
  margin-bottom: 16px;
}

#currentStepTitle {
  margin-right: 15px;
}

/*inputs*/

.inputs {
  margin-bottom: 10px;
}

/*buttons*/

.stepButtons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/*messages*/

#successMessage {
  margin-top: 15px;
  width: 62%;
}

.stepErrorMessages {
  color: $error-text-color;
  font-size: 12px;
}

/* success container */
.submission-success-container {
  max-width: 600px;
  margin: 0 auto;
}
</style>
